import { toast } from "$global-providers/toast-provider";
import { randomUniqueId } from "$routes/_lib/random-id";
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  FormControlLabel,
  MenuItem,
  Select,
  styled,
  Tooltip,
  Typography,
} from "@material-ui/core";
import * as Sentry from "@sentry/react";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRxCollectionEpocusEncounter } from "../../../../global-providers/rxdb-provider/collections/epocus-encounter";
import type { EpocusExam } from "../../../../global-providers/rxdb-provider/collections/epocus-exam";
import { useRxCollectionEpocusExam } from "../../../../global-providers/rxdb-provider/collections/epocus-exam";
import {
  type ExamType,
  NORMAL_WORKSHEET_BY_TYPE,
} from "../../../../global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { useUser } from "../../../../global-providers/user-provider/provider";
import { AccessionIdDisplay } from "../../_lib/accession-id-display";
import { copyToClipboard } from "../../_lib/copy-to-clipboard";
import { matchDeviceFromInstances } from "../../_lib/device-dicom-matching";
import {
  DEVICE_LABELS,
  getDefaultDeviceId,
} from "../../_lib/epocus-exam-constants";
import {
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_PRIMARY,
  EPOCUS_SELECTED_BG,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../_lib/epocus-palette";
import { epocusTheme } from "../../_lib/epocus-theme-provider";
import { useImagingInstancesByAccessionNumber } from "../../_lib/hooks/use-encounter-imaging-instances";
import { PatientSearchField } from "../../_lib/patient-link/patient-search-field";
import type { PatientSearchResult } from "../../_lib/patient-link/use-patient-search";
import { useActiveProgramId } from "../../_lib/use-active-program";
import { useDefaultDevice } from "../../_lib/use-default-device";
import { useEnabledDevices } from "../../_lib/use-enabled-devices";
import { useEnabledExamTypeOptions } from "../../_lib/use-enabled-exam-types";
import { linkToEpocusEncounter, linkToEpocusHome } from "../../links";

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  maxWidth: 600,
  margin: "0 auto",
  [epocusTheme.breakpoints.up("sm")]: {
    padding: 24,
  },
});

const Card = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  borderRadius: 12,
  padding: 16,
  border: `1px solid ${EPOCUS_BORDER}`,
  display: "flex",
  flexDirection: "column",
  gap: 16,
});

const CardLabel = styled(Typography)({
  fontSize: rem(12),
  fontWeight: 700,
  letterSpacing: "0.08em",
  color: EPOCUS_TEXT_SECONDARY,
  textTransform: "uppercase",
});

const ExamGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 8,
});

const ExamTypeBtn = styled(Button)({
  padding: "12px 8px",
  borderRadius: 10,
  fontWeight: 500,
  fontSize: rem(14),
  minHeight: 72,
  textTransform: "none",
  border: `2px solid ${EPOCUS_BORDER}`,
  backgroundColor: EPOCUS_WHITE,
  color: EPOCUS_TEXT_PRIMARY,
});

const ExamIconWrap = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const SubmitButton = styled(Button)({
  borderRadius: 10,
  padding: "14px 24px",
  fontWeight: 700,
  fontSize: rem(16),
  textTransform: "none",
  minHeight: 52,
});

const DEVICE_LOCKED_TOOLTIP =
  "Device was detected from the scan and can't be changed. If this looks wrong, report it to your team.";

export const EpocusNewEncounterPage: FC = () => {
  const navigate = useNavigate();
  const user = useUser();
  const encounterCollection = useRxCollectionEpocusEncounter();
  const examCollection = useRxCollectionEpocusExam();
  const activeProgramId = useActiveProgramId();

  const [selectedPatient, setSelectedPatient] =
    useState<PatientSearchResult | null>(null);
  const [noPatientName, setNoPatientName] = useState(false);
  const examTypeOptions = useEnabledExamTypeOptions();
  const [selectedExamType, setSelectedExamType] = useState<ExamType | null>(
    null,
  );
  const defaultDeviceId = useDefaultDevice();
  const deviceOptions = useEnabledDevices();
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(() =>
    getDefaultDeviceId(deviceOptions, defaultDeviceId),
  );
  const [accessionNumber, setAccessionNumber] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deviceTooltipOpen, setDeviceTooltipOpen] = useState(false);

  // Scan data is authoritative: if imaging instances have already arrived for
  // this accession and carry a recognised device identity, that device is
  // forced and the Select is locked, overriding any manual/default choice.
  const { instances } = useImagingInstancesByAccessionNumber(accessionNumber);
  const scanDetectedDeviceId = useMemo(
    () => matchDeviceFromInstances(instances),
    [instances],
  );
  const deviceLocked = scanDetectedDeviceId !== null;
  const effectiveDeviceId = scanDetectedDeviceId ?? selectedDeviceId;

  // Auto-generate an accession number as soon as the clinician commits to
  // either path — picking a patient OR ticking "No patient". Submit then
  // unblocks the moment an exam type is selected.
  // For the no-patient path the ID also has to make it to the clipboard so
  // the clinician can transcribe it onto the GE machine; do that in the same
  // effect tick (best-effort — failures surface a toast but don't block).
  useEffect(() => {
    if (accessionNumber !== null) return;
    if (selectedPatient === null && !noPatientName) return;

    const id = randomUniqueId(16);
    setAccessionNumber(id);

    if (noPatientName) {
      copyToClipboard(id)
        .then(() => toast.success("Accession ID copied to clipboard"))
        .catch((err) => {
          toast.error(
            "Could not copy automatically — please copy the ID above",
          );
          Sentry.captureException(err);
        });
    }
  }, [selectedPatient, accessionNumber, noPatientName]);

  const handleGenerateAccessionId = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    try {
      const id = randomUniqueId(16);
      setAccessionNumber(id);
      try {
        await copyToClipboard(id);
        toast.success("Accession ID copied to clipboard");
      } catch (clipboardError) {
        // ID is still generated and shown — user can copy manually
        toast.error("Could not copy automatically — please copy the ID above");
        Sentry.captureException(clipboardError);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const canSubmit =
    (noPatientName || selectedPatient !== null) &&
    selectedExamType !== null &&
    effectiveDeviceId !== null &&
    accessionNumber !== null;

  const handleSubmit = async () => {
    if (!canSubmit || !encounterCollection || !examCollection) return;

    setIsSubmitting(true);
    try {
      const encounterId = nanoid();
      const examId = nanoid();
      const now = new Date().toISOString();
      const meta = {
        createdAt: now,
        createdByUserId: user.id,
        updatedAt: now,
        updatedByUserId: user.id,
      };

      await encounterCollection.insert({
        id: encounterId,
        status: "draft",
        deviceId: effectiveDeviceId,
        performerId: user.id,
        programId: activeProgramId,
        elephantPatientId: noPatientName
          ? null
          : (selectedPatient?.patientId ?? null),
        patientName: noPatientName
          ? null
          : (selectedPatient?.displayName ?? null),
        accessionNumber: accessionNumber,
        location: null,
        date: now,
        meta,
      });

      // Derive empty worksheet shape from the "all normal" defaults for this exam type
      const normalData = NORMAL_WORKSHEET_BY_TYPE[selectedExamType];
      const emptyViews = Object.fromEntries(
        Object.keys(normalData.views).map((k) => [k, false]),
      );

      try {
        await examCollection.insert({
          id: examId,
          encounterId,
          signedAt: null,
          signedByUserId: null,
          nonBillable: false,
          nonBillableReason: null,
          amendments: [],
          examType: selectedExamType,
          status: "draft",
          worksheetData: JSON.parse(
            JSON.stringify({
              ...normalData,
              indication: null,
              views: emptyViews,
            }),
          ) as EpocusExam["worksheetData"],
          images: [],
          meta,
        });
      } catch (examError) {
        // Clean up orphaned encounter if exam insert fails
        try {
          const doc = await encounterCollection.findOne(encounterId).exec();
          await doc?.remove();
        } catch (cleanupError) {
          console.error("Failed to clean up orphaned encounter:", cleanupError);
        }
        throw examError;
      }

      navigate(linkToEpocusEncounter(encounterId));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageRoot>
      <Button
        onClick={() => navigate(linkToEpocusHome())}
        style={{
          color: EPOCUS_PRIMARY,
          textTransform: "none",
          fontSize: rem(14),
          padding: "4px 0",
          minWidth: 0,
          alignSelf: "flex-start",
        }}
      >
        ‹ Back
      </Button>

      <Typography
        variant="h5"
        style={{
          fontWeight: 700,
          color: EPOCUS_TEXT_PRIMARY,
          fontSize: rem(24),
        }}
      >
        New Encounter
      </Typography>

      <Card>
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <CardLabel>Patient</CardLabel>
            <FormControlLabel
              control={
                <Checkbox
                  checked={noPatientName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setNoPatientName(e.target.checked);
                    setSelectedPatient(null);
                    setAccessionNumber(null);
                  }}
                  size="small"
                  color="primary"
                />
              }
              label={
                <Typography
                  style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
                >
                  No patient
                </Typography>
              }
              style={{ marginRight: 0 }}
            />
          </Box>
          <PatientSearchField
            selectedPatient={selectedPatient}
            onSelect={setSelectedPatient}
            disabled={noPatientName}
          />
        </Box>

        <Box>
          <CardLabel style={{ marginBottom: 8 }}>Device</CardLabel>
          {deviceLocked ? (
            <ClickAwayListener onClickAway={() => setDeviceTooltipOpen(false)}>
              <Tooltip
                title={DEVICE_LOCKED_TOOLTIP}
                open={deviceTooltipOpen}
                onOpen={() => setDeviceTooltipOpen(true)}
                onClose={() => setDeviceTooltipOpen(false)}
                enterTouchDelay={0}
              >
                {/* Span wrapper: a disabled MUI Select swallows pointer events,
                    so the tooltip listeners have to sit on an enabled element. */}
                <span
                  data-testid="epocus-device-lock"
                  role="button"
                  tabIndex={0}
                  aria-label="Why is this locked?"
                  aria-disabled="true"
                  aria-expanded={deviceTooltipOpen}
                  onClick={() => setDeviceTooltipOpen((open) => !open)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setDeviceTooltipOpen((open) => !open);
                    }
                  }}
                  style={{ display: "block", cursor: "not-allowed" }}
                >
                  <Select
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    displayEmpty
                    disabled
                    value={effectiveDeviceId ?? ""}
                    inputProps={{ "aria-label": "Device" }}
                    renderValue={(value) =>
                      value
                        ? (DEVICE_LABELS[String(value)] ?? String(value))
                        : "Select a device"
                    }
                  >
                    {deviceOptions.map((device) => (
                      <MenuItem key={device.id} value={device.id}>
                        {device.label}
                      </MenuItem>
                    ))}
                  </Select>
                </span>
              </Tooltip>
            </ClickAwayListener>
          ) : (
            <Select
              fullWidth
              variant="outlined"
              margin="dense"
              displayEmpty
              value={selectedDeviceId ?? ""}
              onChange={(e) => setSelectedDeviceId(String(e.target.value))}
              inputProps={{ "aria-label": "Device" }}
              renderValue={(value) =>
                value
                  ? (DEVICE_LABELS[String(value)] ?? String(value))
                  : "Select a device"
              }
            >
              {deviceOptions.map((device) => (
                <MenuItem key={device.id} value={device.id}>
                  {device.label}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>
      </Card>

      {(noPatientName || selectedPatient !== null) && (
        <Card>
          <Box>
            <CardLabel style={{ marginBottom: 4 }}>Accession ID</CardLabel>
            <Typography
              style={{
                fontSize: rem(12),
                color: EPOCUS_TEXT_SECONDARY,
                marginBottom: 12,
              }}
            >
              {noPatientName
                ? "Generate an ID to enter on the GE machine. This links the scan back to this encounter."
                : "Enter this ID on the GE machine to link the scan back to this encounter."}
            </Typography>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              {accessionNumber && (
                <AccessionIdDisplay aria-label="Accession ID">
                  {accessionNumber}
                </AccessionIdDisplay>
              )}
              {noPatientName && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleGenerateAccessionId}
                  disabled={isGenerating}
                  style={{
                    borderRadius: 8,
                    textTransform: "none",
                    fontSize: rem(13),
                    fontWeight: 600,
                    borderColor: EPOCUS_PRIMARY,
                    color: EPOCUS_PRIMARY,
                    whiteSpace: "nowrap",
                  }}
                >
                  {accessionNumber ? "Regenerate & Copy" : "Generate & Copy ID"}
                </Button>
              )}
            </Box>
          </Box>
        </Card>
      )}

      <Card>
        <CardLabel>Select Exam Type</CardLabel>
        <ExamGrid>
          {examTypeOptions.map((option) => (
            <ExamTypeBtn
              key={option.type}
              onClick={() => setSelectedExamType(option.type)}
              aria-pressed={selectedExamType === option.type}
              style={
                selectedExamType === option.type
                  ? {
                      border: `2px solid ${EPOCUS_PRIMARY}`,
                      backgroundColor: EPOCUS_SELECTED_BG,
                      color: EPOCUS_PRIMARY,
                      fontWeight: 700,
                    }
                  : undefined
              }
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                style={{ gap: 6 }}
              >
                <ExamIconWrap aria-hidden>
                  <option.Icon
                    width={28}
                    height={28}
                    fill={
                      selectedExamType === option.type
                        ? EPOCUS_PRIMARY
                        : EPOCUS_ICON_ACTIVE
                    }
                  />
                </ExamIconWrap>
                {option.label}
              </Box>
            </ExamTypeBtn>
          ))}
        </ExamGrid>
      </Card>

      <SubmitButton
        variant="contained"
        color="primary"
        disabled={!canSubmit || isSubmitting}
        onClick={handleSubmit}
        fullWidth
      >
        Create Encounter →
      </SubmitButton>
    </PageRoot>
  );
};
